// src/components/GlobalFileDrop.js

import React, { useState, useCallback, useRef } from 'react';
import './GlobalFileDrop.css';

const GlobalFileDrop = ({ onFileAttach, onError, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef(null);

  const supportedTypes = {
    // Аудио
    'audio/mpeg': 'MP3', 'audio/mp3': 'MP3', 'audio/wav': 'WAV', 'audio/m4a': 'M4A',
    'audio/mp4': 'M4A', 'audio/x-m4a': 'M4A', 'audio/aac': 'AAC', 'audio/flac': 'FLAC',
    'audio/ogg': 'OGG', 'audio/opus': 'OPUS', 'audio/webm': 'WebM Audio',
    // Видео
    'video/mp4': 'MP4', 'video/avi': 'AVI', 'video/mkv': 'MKV', 'video/mov': 'MOV',
    'video/quicktime': 'MOV', 'video/webm': 'WebM Video',
    // Документы
    'application/pdf': 'PDF', 'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'text/plain': 'TXT'
  };

  const getFileCategory = (mimeType, extension) => {
    const safeType = mimeType || '';
    if (safeType.startsWith('audio/')) return 'audio';
    if (safeType.startsWith('video/')) return 'video';
    
    const audioExtensions = ['mp3', 'm4a', 'wav', 'flac', 'ogg', 'aac', 'opus'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'webm', 'mkv'];
    
    if (audioExtensions.includes(extension)) return 'audio';
    if (videoExtensions.includes(extension)) return 'video';
    return 'document';
  };

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;

    let isSupported = !!supportedTypes[selectedFile.type];
    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();

    if (!isSupported) {
      const extensionMimeMap = {
        'mp3': 'audio/mpeg', 'wav': 'audio/wav', 'm4a': 'audio/m4a', 'aac': 'audio/aac',
        'flac': 'audio/flac', 'ogg': 'audio/ogg', 'opus': 'audio/opus', 'mp4': 'video/mp4',
        'avi': 'video/avi', 'mkv': 'video/mkv', 'mov': 'video/mov', 'webm': 'video/webm',
        'pdf': 'application/pdf', 'doc': 'application/msword', 'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'txt': 'text/plain'
      };
      if (extensionMimeMap[fileExtension]) {
        isSupported = true;
      }
    }
    
    if (!isSupported) {
      onError(`Неподдерживаемый тип файла: ${selectedFile.name}.`);
      return;
    }

    if (selectedFile.size > 500 * 1024 * 1024) {
      onError('Файл слишком большой. Максимальный размер: 500MB');
      return;
    }

    const fileData = {
      file: selectedFile,
      id: Date.now() + Math.random(),
      name: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type,
      extension: fileExtension,
      category: getFileCategory(selectedFile.type, fileExtension)
    };

    onFileAttach(fileData, '');
  }, [onFileAttach, onError]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const types = Array.from(e.dataTransfer.types);
    
    // Проверяем, является ли это внутренним перетаскиванием. Если да, игнорируем.
    if (types.some(t => t.startsWith('internal/'))) {
      return;
    }

    // Показываем оверлей, только если это перетаскивание реальных файлов
    if (types.includes('Files')) {
        setIsDragging(true);
    }
  }, []);
    
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Мы скрываем оверлей, только если покинули его самого
    if (e.target === dropZoneRef.current) {
        setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Дополнительная проверка, чтобы случайно не показать оверлей
    const types = Array.from(e.dataTransfer.types);
    if (types.some(t => t.startsWith('internal/'))) {
      return;
    }
    
    if (!isDragging) setIsDragging(true);
  }, [isDragging]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Еще одна проверка на всякий случай
    const types = Array.from(e.dataTransfer.types);
    if (types.some(t => t.startsWith('internal/'))) {
      return;
    }
    
    if (!types.includes('Files')) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  return (
    <div 
      className="global-file-drop"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
    >
      {children}
      
      {isDragging && (
        <div 
            className="global-drop-overlay"
            ref={dropZoneRef}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
          <div className="drop-message">
            <div className="drop-icon">📁</div>
            <h3>Отпустите файл для загрузки</h3>
            <p>Поддерживаются: аудио, видео, PDF, DOC, DOCX, TXT</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalFileDrop;